import Link from "next/link";
import { useRouter } from "next/router";
import { useCallback, useEffect, useState } from "react";
import CookieSettingModal from "./CookieSettingModal";
import { ALL_GRANTED, readConsent, writeConsent } from "@/utils/cookies/cookieConsent";

const COPY = {
  en: {
    message: "We use cookies to ensure you get the best experience on our website.",
    privacy: "Privacy Policy",
    accept: "Accept",
    customize: "Customize",
  },
  ar: {
    message: "نستخدم ملفات تعريف الارتباط لضمان حصولك علي أفضل تجربة علي موقعنا.",
    privacy: "سياسة الخصوصية",
    accept: "قبول",
    customize: "تخصيص",
  },
};

const PRIVACY_HREF = "/static/privacy-policy";

/**
 * Cookie consent banner.
 *
 * Wide and narrow viewports get separate subtrees rather than one responsive
 * tree: the design moves the privacy link out of the sentence and into the
 * button row on mobile, which CSS reordering alone cannot express because the
 * two live in different flex containers.
 */
const CookieConsent = () => {
  const { locale } = useRouter();
  const isEN = locale === "en";
  const copy = isEN ? COPY.en : COPY.ar;
  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // Cookies are only readable client-side — decide after mount.
  useEffect(() => {
    if (!readConsent()) setShowBanner(true);
  }, []);

  const persist = useCallback((consent) => {
    writeConsent(consent);
    setShowModal(false);
    setShowBanner(false);
  }, []);

  const closeModal = useCallback(() => setShowModal(false), []);

  if (!showBanner) return null;

  const font = isEN ? "font-poppins" : "font-noto";

  const acceptBtn = (extra) => (
    <button
      type="button"
      onClick={() => persist(ALL_GRANTED)}
      className={`shrink-0 bg-primary campaign:bg-campaignAccent text-white ${extra}`}
    >
      {copy.accept}
    </button>
  );

  const customizeBtn = (extra) => (
    <button
      type="button"
      onClick={() => setShowModal(true)}
      className={`shrink-0 border border-[#e4e6ea] text-[#4a4a4a] ${extra}`}
    >
      {copy.customize}
    </button>
  );

  return (
    <>
      {!showModal && (
        <section
          className={`fixed bottom-4 inset-x-0 z-[999999999] mx-auto w-[calc(100%-32px)] max-w-[1258px] ${font}`}
        >
          <div className="hidden items-center justify-between gap-[26px] rounded-[8px] bg-white px-[24px] py-[16px] shadow-[0px_-2px_30px_rgba(0,0,0,0.15)] lg:flex">
            <div className="flex items-center gap-[16px] whitespace-nowrap text-[20px]">
              <span className="text-[#4a4a4a]">{copy.message}</span>
              <Link href={PRIVACY_HREF} className="text-primary underline">
                {copy.privacy}
              </Link>
            </div>
            <div className="flex w-[339px] shrink-0 items-center justify-center gap-[16px]">
              {acceptBtn("h-[50px] w-[152px] rounded-[8px] text-[16px]")}
              {customizeBtn("h-[50px] w-[152px] rounded-[8px] text-[16px]")}
            </div>
          </div>

          <div className="flex flex-col gap-[7px] rounded-[8px] bg-white px-[12px] pb-[12px] pt-[15px] shadow-[0px_-2px_30px_rgba(0,0,0,0.15)] lg:hidden">
            <span className="text-[12px] text-[#4a4a4a]">{copy.message}</span>
            <div className="flex items-center justify-center gap-[9px]">
              {acceptBtn("h-[26px] w-[82px] rounded-[4px] text-[12px]")}
              {customizeBtn("h-[26px] w-[82px] rounded-[4px] text-[12px]")}
              <Link
                href={PRIVACY_HREF}
                className="shrink-0 text-[12px] text-primary underline"
              >
                {copy.privacy}
              </Link>
            </div>
          </div>
        </section>
      )}
      {showModal && (
        <CookieSettingModal
          onSave={persist}
          onClose={closeModal}
          locale={locale}
        />
      )}
    </>
  );
};

export default CookieConsent;
